import { useEffect, useState, useCallback } from "react";
import { Bell, Mail, Loader2, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { createReminder, listReminders, deleteReminder } from "@/lib/api";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const ReminderPanel = ({ documentId, scadenze = [] }) => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Count eligible (ISO date) scadenze
  const eligibleCount = scadenze.filter((s) =>
    /^\d{4}-\d{2}-\d{2}$/.test((s.data || "").trim())
  ).length;
  const nonEligibleCount = scadenze.length - eligibleCount;

  const refresh = useCallback(async () => {
    try {
      const items = await listReminders(documentId);
      setSubs(items);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("Failed to load reminders:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    try {
      await createReminder(documentId, email.trim());
      toast.success("Promemoria attivati");
      setEmail("");
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore durante l'attivazione";
      toast.error(typeof msg === "string" ? msg : "Email non valida");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteReminder(id);
      setSubs((xs) => xs.filter((x) => x.id !== id));
      toast.success("Promemoria disattivato");
    } catch {
      toast.error("Errore durante la disattivazione");
    }
  };

  return (
    <section
      data-testid="section-reminders"
      className="mt-6 p-6 md:p-8 bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-2xl animate-fade-up"
    >
      <div className="flex items-start gap-3 mb-5 flex-wrap justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center shrink-0">
            <Bell size={18} />
          </div>
          <div>
            <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">
              Promemoria email
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Ti avvisiamo 7, 3 e 1 giorno prima di ogni scadenza con data certa
            </p>
          </div>
        </div>
        <span
          data-testid="eligible-count"
          className="px-3 py-1 rounded-full bg-white border border-indigo-200 text-indigo-700 text-xs font-bold"
        >
          {eligibleCount} programmabili
        </span>
      </div>

      {eligibleCount === 0 ? (
        <div className="p-4 rounded-lg bg-white border border-amber-200 text-sm text-amber-800 flex items-start gap-2.5">
          <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <b>Nessuna scadenza con data certa.</b> Le scadenze di questo contratto sono
            espresse come regole temporali (es. "entro 60 giorni dalla fattura"). Per attivare
            i promemoria serve una data ISO YYYY-MM-DD nel testo del contratto.
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="reminder-form">
          <div className="flex-1 relative">
            <Mail
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="email"
              required
              placeholder="tua@email.it"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="reminder-email-input"
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition-all"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="reminder-submit-btn"
            className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white font-semibold px-5 py-2.5 rounded-md transition-colors text-sm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell size={14} />}
            Attiva promemoria
          </button>
        </form>
      )}

      {nonEligibleCount > 0 && eligibleCount > 0 && (
        <p className="mt-3 text-xs text-slate-500">
          {nonEligibleCount} scadenze non sono programmabili (date relative).
        </p>
      )}

      {!loading && subs.length > 0 && (
        <div className="mt-6">
          <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-700 mb-3">
            Promemoria attivi
          </h4>
          <ul className="space-y-2">
            {subs.map((s, i) => {
              const sentTotal = Object.keys(s.sent_log || {}).length;
              return (
                <li
                  key={s.id}
                  data-testid={`reminder-${i}`}
                  className="flex items-center justify-between gap-3 p-3 bg-white border border-slate-200 rounded-lg"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 text-sm truncate">
                        {s.email}
                      </div>
                      <div className="text-xs text-slate-500 font-mono">
                        Attivato il {formatDate(s.created_at)} · {sentTotal} email inviate
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => onDelete(s.id)}
                    data-testid={`reminder-delete-${i}`}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors shrink-0"
                    aria-label="Disattiva promemoria"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

export default ReminderPanel;
